import { AnyAction, ThunkAction, createAction } from '@reduxjs/toolkit';
import {
  ComponentDragState,
  EditorArea,
  HighlightedSpacingDirection,
  IsolationState,
  ManipulationRootState,
  RightSideTab,
} from './types';
import { selectHoveredComponentId, selectIsolatedComponentId } from './selectors';

/**
 * Action that defines the view in which the user is currently interacting.
 * The component selection and component isolation are expected to be
 * in the context of this view.
 *
 * Please dispatch this actien whenever the view ID in the URL changes,
 * as the view in the store state should match the one in the URL.
 */
export const setCurrentView = createAction<number | null>('manipulation/setCurrentView');

/**
 * Action that sets the currently highlighted component instance on the canvas
 * and the tree view, i.e. the one the user currently has their cursor on.
 * If null, then clears the highlighted component.
 */
export const setHoveredComponent = createAction<string | null>('manipulation/setHoveredComponent');

/**
 * Simple thunk that dispatches `setHoveredComponent` action but only if the the component
 * is not already set hovered. This only exists to minimize the number of states on Redux dev tools
 * for easier inspection and memory usage.
 */
export function hoverComponent(
  componentId: string | null,
): ThunkAction<boolean, ManipulationRootState, unknown, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const hoveredComponentId = selectHoveredComponentId(state);
    if (hoveredComponentId === componentId) {
      // Action would not result in changes
      return false;
    }
    dispatch(setHoveredComponent(componentId));
    return true;
  };
}

/**
 * Action that selects a component instance with the given ID,
 * or clears the selection.
 */
export const setComponentSelection = createAction<string | null>('manipulation/setComponentSelection');

/**
 * Action that selects a component instance with the given ID,
 * or clears the selection.
 */
export const setInitialComponentSelection = createAction<string | null>('manipulation/setInitialComponentSelection');

/**
 * Action that clears any component selection, resetting to either null,
 * or to the currently isolated component (if in isolation mode).
 */
export const clearComponentSelection = () => setComponentSelection(null);

/**
 * Action that sets or updates the dragging state of a component.
 */
export const dragComponent = createAction<ComponentDragState>('manipulation/dragComponent');

/**
 * Action that ends the component dragging state.
 */
export const stopComponentDrag = createAction<void>('manipulation/stopComponentDrag');

/**
 * Action that opens a component instance with the given ID in
 * either full or light isolation mode.
 */
export const openComponentIsolationMode = createAction(
  'manipulation/openComponentIsolationMode',
  (componentId: string, mode: 'full' | 'light', editedStyleClass: string | null = null) => {
    const isolation: IsolationState = { componentId, mode, editedStyleClass };
    return {
      payload: isolation,
    };
  },
);

/**
 * Action that exits the isolation mode.
 */
export const closeComponentIsolationMode = createAction<void>('manipulation/closeComponentIsolationMode');

/**
 * Thunk that either opens the given component in the isolation mode,
 * or exits the isolation mode if this component is already isolated.
 */
export function toggleComponentIsolationMode(
  componentId: string,
  mode: 'full' | 'light',
): ThunkAction<boolean, ManipulationRootState, unknown, AnyAction> {
  return (dispatch, getState) => {
    const state = getState();
    const isolatedComponentId = selectIsolatedComponentId(state);
    if (isolatedComponentId === componentId) {
      dispatch(closeComponentIsolationMode());
      return false;
    }
    dispatch(openComponentIsolationMode(componentId, mode));
    return true;
  };
}

/**
 * Action for changing the edited style class while the (full) isolation mode is open.
 * It does nothing if not currently in the isolation mode.
 */
export const setComponentIsolationEditedStyleClass = createAction<string | null>(
  'manipulation/setComponentIsolationEditedStyleClass',
);

/**
 * Action for changing the currently focused UI area.
 */
export const setFocusedEditorArea = createAction<EditorArea>('manipulation/setFocusedEditorArea');

/**
 * Action for changing the currently active right side tab.
 */
export const activateRightSideTab = createAction<RightSideTab>('manipulation/activateRightSideTab');

/**
 * Action for setting the highlighted spacing direction.
 */
export const setHighlightedSpacingDirection = createAction<HighlightedSpacingDirection | null>(
  'manipulation/setHighlightedSpacingDirection',
);

/**
 * Action for setting the adapter data mapping dialog open state.
 */
export const setAdapterDataMappingOpen = createAction<boolean>('manipulation/setAdapterDataMappingOpen');
